/**
 * metadata.media_spec_id を持つ属性 option が新たに選択されたとき、
 * その媒体規格を「媒体要件」へ適用するかを確認ダイアログで提案するフック。
 *
 * org-admin の属性管理 (MediaSpecMetaPicker) で option に紐付けられた
 * 媒体規格を、選択時に settings.media_platform_id / media_menu_id /
 * media_spec_id へ反映する。反映後のアスペクト比追従・解像度の自動昇格・
 * トリミング目標の設定は MediaRequirementsAccordion 側の既存 effect が担う
 * ため、ここでは 3 つの ID とガイドラインの反映のみ行う。
 *
 * overall_impression → aspect_ratio の既存提案 (GenerationWorkspace) と同じく、
 * ユーザーは「そのまま」を選んで適用しないことも許される。
 */

import { useCallback, useEffect, useRef, useState, type MutableRefObject } from 'react';
import { fetchAssetSpecById, isVideoSpec, type AssetSpec } from '../../../features/media/api';
import { findNodeByKey } from '../utils/templateCapture';
import type { AttributeTreeNode, GenerationSettings } from '../state/types';

export type MediaSpecSuggestion = {
  optionLabel: string;
  spec: AssetSpec;
};

type SpecLinkedSelection = { id: string; label: string; specId: string };

/** 1 フィールドノード (トップレベル group の子) の選択値を再帰的に走査する */
function walkFieldNode(
  node: AttributeTreeNode,
  value: unknown,
  out: SpecLinkedSelection[],
): void {
  if (value == null) return;
  if (typeof value === 'string') {
    if (!value || value === 'none') return;
    const option = findNodeByKey(node.children, value);
    const specId = option?.metadata?.media_spec_id;
    if (option && typeof specId === 'string' && specId) {
      out.push({ id: option.id, label: option.label, specId });
    }
    return;
  }
  // selection='group' な入れ子 group (person 等) は { childKey: 選択値 } のオブジェクト
  if (typeof value === 'object') {
    const record = value as Record<string, unknown>;
    for (const child of node.children ?? []) {
      walkFieldNode(child, record[child.key], out);
    }
  }
}

/** 選択中の option ノードのうち metadata.media_spec_id を持つものを集める */
export function collectSpecLinkedSelections(
  tree: AttributeTreeNode[] | undefined,
  attributes: Record<string, unknown> | undefined,
): SpecLinkedSelection[] {
  const out: SpecLinkedSelection[] = [];
  if (!tree?.length || !attributes) return out;
  for (const root of tree) {
    for (const field of root.children ?? []) {
      walkFieldNode(field, attributes[field.key], out);
    }
  }
  return out;
}

export function useMediaSpecSuggestion({
  attributeTree,
  attributes,
  setSettings,
  suppressOnceRef,
}: {
  attributeTree: AttributeTreeNode[];
  attributes: Record<string, unknown> | undefined;
  setSettings: (updater: (prev: GenerationSettings) => GenerationSettings) => void;
  /** ガチャ等の一括選択で提案を 1 回スキップするためのフラグ */
  suppressOnceRef: MutableRefObject<boolean>;
}) {
  const [suggestion, setSuggestion] = useState<MediaSpecSuggestion | null>(null);
  // 前回レンダー時点で選択されていた spec 紐付き option の id 集合。
  // null = ベースライン未確立 (ツリー未ロード)。確立後の差分のみ提案対象にする
  // ことで、リロード直後の永続化済み選択では発火させない。
  const prevSelectedIdsRef = useRef<Set<string> | null>(null);

  useEffect(() => {
    if (!attributeTree.length) return;
    const selected = collectSpecLinkedSelections(attributeTree, attributes);
    const prev = prevSelectedIdsRef.current;
    prevSelectedIdsRef.current = new Set(selected.map((s) => s.id));
    if (prev === null) return;
    if (suppressOnceRef.current) {
      suppressOnceRef.current = false;
      return;
    }
    const fresh = selected.filter((s) => !prev.has(s.id));
    if (fresh.length === 0) return;
    // 一括適用などで複数増えた場合は最後の 1 件のみ提案する
    const target = fresh[fresh.length - 1];

    let cancelled = false;
    fetchAssetSpecById(target.specId)
      .then((spec) => {
        if (cancelled || !spec || isVideoSpec(spec)) return;
        setSuggestion({ optionLabel: target.label, spec });
      })
      .catch(() => {
        // 規格が削除済み等で取得できない場合は提案しない
      });
    return () => {
      cancelled = true;
    };
  }, [attributeTree, attributes, suppressOnceRef]);

  const accept = useCallback(() => {
    setSuggestion((current) => {
      if (current) {
        const spec = current.spec;
        setSettings((prev) => ({
          ...prev,
          media_platform_id: spec.platform_id ?? null,
          media_menu_id: spec.menu_id ?? null,
          media_spec_id: spec.id,
          media_guidelines: spec.guidelines || [],
          media_menu_guidelines: spec.menu_guidelines || [],
        }));
      }
      return null;
    });
  }, [setSettings]);

  const dismiss = useCallback(() => setSuggestion(null), []);

  return { suggestion, accept, dismiss };
}
